package com.damda.design_system.ui.views.foods

import androidx.compose.runtime.Composable
import com.damda.design_system.core.ImageResourceLoader
import com.damda.design_system.ui.views.compose_migrations.images.JSImage
import com.damda.design_system.ui.views.compose_migrations.layouts.JSColumn
import com.damda.design_system.ui.views.compose_migrations.layouts.JSSpacer
import com.damda.design_system.ui.views.compose_migrations.texts.StyledText
import com.damda.design_system.ui.views.core.JSViewPadding
import com.damda.design_system.ui.views.lists.FoodItem
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.CSSNumeric
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.cursor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexShrink
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.overflowX
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgb
import org.jetbrains.compose.web.css.whiteSpace
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.AttrBuilderContext
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

@Composable
fun JSFoodList(
    imageResourceLoader: ImageResourceLoader,
    items: List<FoodItem>,
    onAddToCartClick: (FoodItem) -> Unit,
    onCardClick: (FoodItem) -> Unit,
    cardWidth: CSSNumeric = 190.px,
    cardHeight: CSSNumeric = 250.px,
    contentWidth: CSSNumeric = 172.px
) {
    Div({
        classes("hide-scrollbar")
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Row)
            overflowX("auto") // 가로 스크롤 가능하게 설정
            padding(30.px) // 초기 horizontal 패딩값 설정
            boxSizing("border-box")
            width(100.percent)
            whiteSpace("nowrap") // 아이템들이 한 줄로 나열되도록 설정
        }
        style {
            property("::-webkit-scrollbar", "display:none")
            property("-ms-overflow-style", "none") // IE and Edge
            property("scrollbar-width", "none") // Firefox
        }
    }) {
        items.forEach { item ->
            Div({
                style {
                    width(cardWidth) // 고정된 너비
                    height(auto) // 높이는 내용에 따라 자동으로 조정
                    margin(4.px) // 카드 간의 간격 설정
                    flexShrink(0) // Flexbox에서 아이템 크기가 줄어들지 않도록 설정
                }
            }) {
                JSFoodItemCard(
                    imageResourceLoader = imageResourceLoader,
                    foodItem = item,
                    onAddToCartClick = { onAddToCartClick(item) },
                    onCardClick = onCardClick,
                    cardWidth = cardWidth,
                    cardHeight = cardHeight,
                    contentWidth = contentWidth,
                    contentHeight = 105.px // 여기서 contentHeight를 고정된 값으로 설정
                )
            }
        }
    }
}

@Composable
fun JSFoodItemCard(
    imageResourceLoader: ImageResourceLoader,
    foodItem: FoodItem,
    onAddToCartClick: () -> Unit,
    onCardClick: (FoodItem) -> Unit,
    cardWidth: CSSNumeric = 190.px,
    cardHeight: CSSNumeric = 250.px,
    contentWidth: CSSNumeric = 160.px,
    contentHeight: CSSNumeric = 105.px,
    imageHeight: CSSNumeric = 145.px,
    modifier: AttrBuilderContext<HTMLElement>? = null
) {
    Div({
        style {
            width(cardWidth)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Color.white)
            borderRadius(16.px)
            property("box-shadow", "0px 4px 4px rgba(0, 0, 0, 0.25)")
            cursor("pointer")
            property("overflow", "hidden")
        }
        modifier?.invoke(this)
        onClick { onCardClick(foodItem) }
    }) {
        Div({
            style {
                width(cardWidth)
                height(cardHeight)
            }
        }) {
            JSColumn {
                FoodImage(
                    imageResourceLoader = imageResourceLoader,
                    imageName = foodItem.imageName,
                    cardWidth = cardWidth,
                    imageHeight = imageHeight
                )
                FoodDescription(
                    foodItem = foodItem,
                    contentWidth = contentWidth,
                    contentHeight = contentHeight,
                    cardWidth = cardWidth
                )
            }
        }
    }
}

@Composable
fun FoodImage(
    imageResourceLoader: ImageResourceLoader,
    imageName: String,
    cardWidth: CSSNumeric,
    imageHeight: CSSNumeric
) {
    Div({
        style {
            width(cardWidth)
            height(imageHeight)
        }
    }) {
        JSImage(
            imageResourceLoader = imageResourceLoader,
            imageResName = imageName,
            attrs = {
                style {
                    width(100.percent)
                    height(100.percent)
                    property("object-fit", "cover")
                    borderRadius(13.px)
                }
            }
        )
    }
}

@Composable
fun FoodDescription(
    foodItem: FoodItem,
    contentWidth: CSSNumeric,
    contentHeight: CSSNumeric,
    cardWidth: CSSNumeric
) {
    Div({
        style {
            width(cardWidth)
            height(contentHeight)
            alignItems(AlignItems.Normal)
            overflow("hidden")
            flexShrink(0)
        }
    }) {
        JSColumn {
            StyledText(
                text = foodItem.title,
                fontSize = 14.px,
                fontWeight = "bold",
                maxLines = 2,
                color = rgb(51, 51, 51),
                widthValue = contentWidth,
                padding = JSViewPadding(16.px, 0.px, 16.px, 0.px)
            )
            JSSpacer(height = 2.px)
            StyledText(
                text = foodItem.subtitle,
                fontSize = 10.px,
                color = rgb(176, 176, 176),
                maxLines = 2,
                widthValue = contentWidth,
                padding = JSViewPadding(16.px, 0.px, 16.px, 0.px)
            )
            JSSpacer(height = 10.px)
            StyledText(
                text = foodItem.price,
                fontSize = 18.px,
                fontWeight = "bold",
                color = rgb(51, 51, 51),
                padding = JSViewPadding(16.px, 0.px, 16.px, 0.px)
            )
        }
    }
}

